import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { getAllowedAreas } from 'api/users';

// ==============================|| ROLE-BASED ACCESS CONTROL ||============================== //

const SUPERVISOR_EQUIVALENTS = new Set(['supervisor', 'comercial']);
const INFORMES_AREA_SLUG = 'informes';
const RESUMEN_OBRAS_SUBAREA_SLUG = 'resumen-obras';

export const usePermissions = () => {
  const { user } = useAuth();

  const hasRole = (requiredRole) => {
    if (!requiredRole) return true;
    if (!user) return false;
    if (user.role === 'admin') return true;
    const normalized = String(requiredRole || '').trim().toLowerCase();
    if (!normalized) return true;
    if (normalized === 'supervisor') {
      return SUPERVISOR_EQUIVALENTS.has(user.role);
    }
    return user.role === normalized;
  };

  const canAccess = (item) => hasRole(item.requiredRole);

  const isAdmin = () => user?.role === 'admin';
  const isSupervisor = () => SUPERVISOR_EQUIVALENTS.has(user?.role);
  const isOperario = () => user?.role === 'operario';
  const isContabilidad = () => user?.role === 'contabilidad';
  const isComercial = () => user?.role === 'comercial';

  return {
    hasRole,
    canAccess,
    isAdmin,
    isSupervisor,
    isOperario,
    isContabilidad,
    isComercial,
    userRole: user?.role
  };
};

export const hasAreaSubareaAccess = (allowedAreas = [], areaSlug, subareaSlug, userRole = '') => {
  if (String(userRole || '').trim().toLowerCase() === 'admin') {
    return true;
  }

  const normalizedAreaSlug = String(areaSlug || '').trim().toLowerCase();
  const normalizedSubareaSlug = String(subareaSlug || '').trim().toLowerCase();
  if (!normalizedAreaSlug || !normalizedSubareaSlug || !Array.isArray(allowedAreas)) {
    return false;
  }

  return allowedAreas.some((area) => {
    if (String(area?.slug || '').trim().toLowerCase() !== normalizedAreaSlug) {
      return false;
    }

    const subareas = Array.isArray(area?.subareas) ? area.subareas : [];
    return subareas.some((subarea) => {
      if (typeof subarea === 'string') {
        return String(subarea || '').trim().toLowerCase() === normalizedSubareaSlug;
      }
      return String(subarea?.slug || '').trim().toLowerCase() === normalizedSubareaSlug;
    });
  });
};

export const useAllowedAreas = () => {
  const { user } = useAuth();
  const normalizedRole = String(user?.role || '').trim().toLowerCase();
  const isAdmin = normalizedRole === 'admin';
  const [allowedAreas, setAllowedAreas] = React.useState([]);
  const [loading, setLoading] = React.useState(Boolean(user) && !isAdmin);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let active = true;

    if (!user) {
      setAllowedAreas([]);
      setLoading(false);
      setError('');
      return () => {
        active = false;
      };
    }

    if (isAdmin) {
      setAllowedAreas([]);
      setLoading(false);
      setError('');
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setError('');

    getAllowedAreas()
      .then((areas) => {
        if (!active) return;
        setAllowedAreas(Array.isArray(areas) ? areas : []);
      })
      .catch((err) => {
        if (!active) return;
        setAllowedAreas([]);
        setError(err?.message || 'No se pudieron cargar los accesos');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isAdmin, user]);

  return { allowedAreas, loading, error };
};

const OPERARIO_RESTRICTED_MENU_IDS = new Set([
  'oportunidades',
  'visitas',
  'pendiente-boletin',
  'terminados',
  'rechazadas'
]);

const filterMenuItems = (items = [], isOperario, allowedAreas = [], userRole = '') => {
  if (!Array.isArray(items)) return [];
  return items.reduce((acc, item) => {
    const nextItem = { ...item };
    if (Array.isArray(nextItem.children)) {
      nextItem.children = filterMenuItems(nextItem.children, isOperario, allowedAreas, userRole);
    }
    if (isOperario && nextItem.id && OPERARIO_RESTRICTED_MENU_IDS.has(nextItem.id)) {
      return acc;
    }
    if (
      nextItem.id === 'informes-resumen-obras' &&
      !hasAreaSubareaAccess(allowedAreas, INFORMES_AREA_SLUG, RESUMEN_OBRAS_SUBAREA_SLUG, userRole)
    ) {
      return acc;
    }
    acc.push(nextItem);
    return acc;
  }, []);
};

export const filterMenuByPermissions = (menuItems, userRole, allowedAreas = []) => {
  if (!menuItems || !Array.isArray(menuItems)) return [];
  const normalizedRole = String(userRole || '').trim().toLowerCase();
  const isOperario = normalizedRole === 'operario';
  return filterMenuItems(menuItems, isOperario, allowedAreas, normalizedRole);
};

export const withRoleProtection = (WrappedComponent, requiredRole = null) => {
  return function ProtectedComponent(props) {
    const { hasRole } = usePermissions();
    const { user } = useAuth();

    if (!requiredRole) {
      return <WrappedComponent {...props} />;
    }

    const hasRequiredRole = Array.isArray(requiredRole)
      ? requiredRole.some((role) => hasRole(role))
      : hasRole(requiredRole);

    if (!hasRequiredRole) {
      return <Navigate to="/dashboard/default" replace state={{ fromRestricted: true, requiredRole, currentRole: user?.role }} />;
    }

    return <WrappedComponent {...props} />;
  };
};

export default usePermissions;
